import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Pagination,
  LoadedContent,
  LinearProgress,
  TableContainer,
  TableHeaderBulkSelectCheckbox,
} from '@occc/ui-design-system';
import { RetailerNetworkLocationFragment } from '@occc/graphql';
import { useBulkSelect } from '@occc-app/hooks/useBulkSelect';
import { Can } from '@occc-app/App/PolicyBasedAccessControl';
import { AddLocationsToRetailerNetworkLocationGroup } from '@occc-app/App/PolicyBasedAccessControl/userActions';

import { AFTER_PARAM_NAME } from './retailer-network-locations.constants';
import LocationListItem from './location-list-item.component';

import { useGetRetailerNetworkLocations } from '../../../../../data-access/queries';

type LocationListProps = ReturnType<typeof useBulkSelect> & {
  locationsData: ReturnType<typeof useGetRetailerNetworkLocations>;
  handleOpenAddLocationsToLocationGroupDialog: () => void;
  onRemoveLocationMenuItemClick: (locationUuid: string) => void;
  handleOpenLocationDetailsPage: (locationUuid: string) => void;
  retailerNetworkUuid: string;
};

export const LocationList = ({
  locationsData,
  bulkSelectedItems,
  handleBulkSelectMultiple,
  handleItemSelect,
  handleOpenAddLocationsToLocationGroupDialog,
  onRemoveLocationMenuItemClick,
  handleOpenLocationDetailsPage,
  retailerNetworkUuid,
}: LocationListProps) => {
  const { loading: locationsLoading, error, data, paginationProps } = locationsData;
  const currentPageLocationUuids =
    data?.map((item: RetailerNetworkLocationFragment) => item.location.uuid) || [];

  const renderData = (items: RetailerNetworkLocationFragment[]) => (
    <TableContainer sx={{ borderRadius: 0 }}>
      {locationsLoading && <LinearProgress />}
      <Table>
        <TableHead>
          <TableRow>
            <Can I={AddLocationsToRetailerNetworkLocationGroup({ uuid: retailerNetworkUuid })}>
              <TableCell padding='checkbox'>
                <TableHeaderBulkSelectCheckbox
                  bulkSelectedItems={bulkSelectedItems}
                  handleBulkSelectMultiple={handleBulkSelectMultiple}
                  currentPageItems={currentPageLocationUuids}
                />
              </TableCell>
            </Can>
            <TableCell>Site ID</TableCell>
            <TableCell>Site Name</TableCell>
            <TableCell align='right'>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((location) => (
            <LocationListItem
              retailerNetworkUuid={retailerNetworkUuid}
              location={location}
              key={location.uuid}
              isItemSelected={bulkSelectedItems.has(location.location.uuid)}
              handleItemSelect={handleItemSelect}
              handleOpenAddLocationsToLocationGroupDialog={
                handleOpenAddLocationsToLocationGroupDialog
              }
              onRemoveLocationMenuItemClick={onRemoveLocationMenuItemClick}
              handleOpenLocationDetailsPage={handleOpenLocationDetailsPage}
            />
          ))}
        </TableBody>
      </Table>
      <Pagination {...paginationProps} />
    </TableContainer>
  );

  return (
    <LoadedContent<RetailerNetworkLocationFragment>
      error={error}
      loading={locationsLoading}
      items={data}
      renderData={renderData}
      afterParamName={AFTER_PARAM_NAME}
    />
  );
};
